import PieChart from '@risk-smart/themed-cloudscape-components/pie-chart';
import i18n from '@risksmart-app/i18n/src/i18n';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import PopoverFooter from 'src/components/popover-footer';
import { useDashboardStore } from 'src/pages/dashboards/useDashboardStore';

import { NoWidgetData } from '../../../widgets/NoWidgetData';
import { useAggregateCategories } from '../../hooks/useAggregateCategories';
import { useGetWidgetData } from '../../hooks/useGetWidgetData';
import type {
  CategoryType,
  DataSourceItem,
  UnratedCategoryType,
  WidgetDataSource,
} from '../../types';
import type { GigaPieWidgetProps } from './GigaPieWidgetProps';
import type { PieSegment } from './hooks/usePieChartData';
import { usePieChartData } from './hooks/usePieChartData';
import styles from './styles.module.scss';

export const GigaPieWidgetCloudscape = <
  TDataSource extends WidgetDataSource,
  TCategory extends CategoryType,
>({
  donut,
  dataSource,
  variables,
  categoryGetter,
  dateFilterOptions,
  onClickUrl,
  categoryRatingTranslationKey,
  categoryOverrideFunction,
  countFormatter,
  propertyFilterQuery,
  aggregationType,
  aggregationField,
  noClickthroughMessageContent,
}: GigaPieWidgetProps<TDataSource, TCategory>) => {
  const { filters } = useDashboardStore();
  const {
    tableProps: { allItems },
    loading,
  } = useGetWidgetData({
    dataSource,
    variables,
    dateFilterOptions,
    propertyFilterQuery,
  });
  const highlightedSegment = useRef<null | PieSegment<
    DataSourceItem<TDataSource>,
    TCategory | UnratedCategoryType
  >>(null);

  const total = useMemo(
    () => (allItems ? (allItems?.length ?? 0) : 0),
    [allItems]
  );

  const navigate = useNavigate();

  const countedCategories = useAggregateCategories({
    dataSource,
    items: allItems,
    dateFilterOptions,
    categoryGetter,
    aggregationType,
    aggregationField,
  });

  const { t } = useTranslation();

  const pieChartData = usePieChartData(countedCategories, {
    categoryOverrideFunction,
    categoryKey: categoryRatingTranslationKey,
  });

  const description = i18n.format(
    t(dataSource.entityNamePlural),
    'capitalizeAll'
  );

  const handleClick = useCallback(() => {
    if (onClickUrl && highlightedSegment.current) {
      const url = onClickUrl(highlightedSegment.current.category, filters);
      if (url) {
        navigate(url);
      }
    }
  }, [highlightedSegment, navigate, onClickUrl, filters]);

  let dataLabel = t('dashboard.count');
  if (aggregationType) {
    switch (aggregationType) {
      case 'sum':
        dataLabel = 'Sum';
        break;
      case 'mean':
        dataLabel = 'Mean';
        break;
      case 'count':
        dataLabel = t('dashboard.count');
        break;
    }
  }

  return (
    <div
      className={styles.pieChart}
      onClick={handleClick}
      aria-describedby={'awsui-pie-chart'}
    >
      <PieChart
        statusType={loading ? 'loading' : 'finished'}
        variant={donut ? 'donut' : 'pie'}
        hideLegend={true}
        onHighlightChange={(e) =>
          (highlightedSegment.current = e.detail.highlightedSegment)
        }
        hideFilter={true}
        innerMetricValue={total.toString()}
        innerMetricDescription={description}
        data={pieChartData}
        detailPopoverContent={(datum, sum) => [
          countFormatter
            ? countFormatter(datum, sum)
            : { key: dataLabel, value: datum.value },
          {
            key: t('dashboard.percentage'),
            value: `${((datum.value / sum) * 100).toFixed(0)}%`,
          },
        ]}
        detailPopoverFooter={
          !onClickUrl && noClickthroughMessageContent
            ? (_) => <PopoverFooter message={noClickthroughMessageContent} />
            : undefined
        }
        segmentDescription={(datum, sum) =>
          countFormatter ? countFormatter(datum, sum).value : `${datum.value}`
        }
        ariaDescription={description}
        empty={<NoWidgetData />}
      />
    </div>
  );
};
